/**
 * A collapsible section header for one of the 53 endpoint families.
 *
 * 1,062 rows in one flat list is a haystack. The registry already groups them
 * by tag ("TikTok-Web-API", "Douyin-App-V3", ...), and a family is the unit
 * people actually reason about: an outage is almost never one endpoint, it is
 * one upstream provider taking a whole family down with it.
 *
 * The header carries the family's health counts computed over ALL its
 * endpoints, not just the ones the current status filter leaves visible. A
 * count that moved when you toggled a filter chip would be describing the
 * filter rather than the API. `visibleCount of endpointCount` is shown
 * separately so the gap between the two is explicit instead of confusing.
 */
import { useI18n } from '../i18n'
import type { TFunction } from '../i18n'
import { HEALTH_STATES } from '../api/types'
import type { HealthState } from '../api/types'
import {
  BUCKET_STYLES,
  COLUMN_BUCKET,
  HEALTH_HINT_KEYS,
  HEALTH_LABEL_KEYS,
  HEALTH_STYLES,
  STATUS_COLUMNS,
  numberFormat,
} from './statusColors'
import type { StatusColumn } from './statusColors'
import { ENDPOINT_GRID, ROW_HEIGHT } from './EndpointRow'
import { StatusRatioBar } from './StatusRatioBar'

/** Worst first, matching the overview tiles and the table's default sort. */
const CHIP_ORDER: readonly HealthState[] = ['down', 'degraded', 'unknown', 'healthy']

export interface FamilyBucket {
  family: string
  /** Rows the current status filter leaves on screen. */
  visibleCount: number
  /** Rows the family has in the registry, filter or no filter. */
  endpointCount: number
  /** Endpoints in the family that Cloudflare actually returned data for. Most
   *  families have none in a short window, and the difference between "none"
   *  and "some" is the difference between a missing total and a partial one. */
  measuredCount: number
  /** Sum over the measured endpoints, or `null` when none were measured. A 0
   *  here would be a fabricated measurement on the one number a collapsed
   *  family row shows — and it would be wrong on most of the 53 families. */
  totalRequests: number | null
  /** Per-status-bucket sums over the measured endpoints, or `null` when none
   *  were measured. Same rule as `totalRequests`: a row of zeros on a family we
   *  never got data for would be a fabricated measurement, and most of the 53
   *  families have no data at all in a short window. */
  statusTotals: Record<StatusColumn, number> | null
  /** Over every endpoint in the family, unaffected by the status filter. */
  healthCounts: Record<HealthState, number>
  /** The worst state present — what the family sorts on. */
  worstHealth: HealthState
}

export interface FamilyGroupProps {
  bucket: FamilyBucket
  expanded: boolean
  onToggle: (family: string) => void
  style?: React.CSSProperties
}

export function FamilyGroup({ bucket, expanded, onToggle, style }: FamilyGroupProps) {
  const { t } = useI18n()
  return (
    <div
      role="row"
      data-testid="family-row"
      data-family={bucket.family}
      style={{ height: ROW_HEIGHT, ...style }}
      className="border-b border-border bg-surface"
    >
      {/*
        The same grid template as the endpoint rows, not the flex row this used
        to be. A collapsed family now answers "what is this family made of"
        without being opened, and its numbers sit under the very column headers
        that name them — a total parked at the far right of a flex row was a
        number with no column.
      */}
      <button
        type="button"
        aria-expanded={expanded}
        onClick={() => onToggle(bucket.family)}
        className={`${ENDPOINT_GRID} h-full w-full text-left hover:bg-bg`}
      >
        <span className="flex items-center gap-2 text-xs text-muted">
          <span aria-hidden="true" className="w-3 shrink-0">
            {expanded ? '▾' : '▸'}
          </span>
          {CHIP_ORDER.map((state) => {
            const count = bucket.healthCounts[state] ?? 0
            // A zero chip on 53 rows is 53 zeros of visual noise; the state is
            // still reachable through the overview tiles, which never hide it.
            if (count === 0) return null
            const chip = HEALTH_STYLES[state]
            return (
              <span
                key={state}
                data-testid={`family-health-${state}`}
                title={t('dashboard.family.chipTitle', {
                  count,
                  // Lower-cased for English mid-sentence; a no-op in Chinese,
                  // which has no case to fold.
                  state: t(HEALTH_LABEL_KEYS[state]).toLowerCase(),
                  hint: t(HEALTH_HINT_KEYS[state]),
                })}
                className="flex items-center gap-1"
              >
                <span aria-hidden="true" className={`h-2 w-2 rounded-full ${chip.bg}`} />
                <span className="num">{numberFormat.format(count)}</span>
              </span>
            )
          })}
        </span>

        <span className="flex min-w-0 items-baseline gap-2">
          <span className="truncate text-sm font-semibold text-fg">{bucket.family}</span>
          <span data-testid="family-count" className="num shrink-0 text-xs text-muted">
            {t('dashboard.family.count', {
              visible: numberFormat.format(bucket.visibleCount),
              total: numberFormat.format(bucket.endpointCount),
            })}
          </span>
        </span>

        <span
          data-testid="family-total"
          title={totalTitle(bucket, t)}
          className={`num text-right text-xs tabular-nums ${
            bucket.totalRequests === null ? 'text-muted' : 'text-fg'
          }`}
        >
          {bucket.totalRequests === null ? '—' : numberFormat.format(bucket.totalRequests)}
        </span>

        {STATUS_COLUMNS.map((b) => {
          const count = bucket.statusTotals?.[b] ?? null
          return (
            <span
              key={b}
              data-testid={`family-status-${b}`}
              // `null` is "we measured nothing in this family", a 0 is "we
              // measured, and this bucket saw nothing". Same distinction the
              // endpoint rows draw, and the same reason: this monitor is
              // passive, so silence is never evidence.
              title={
                count === null
                  ? t('dashboard.family.statusNone', { bucket: b })
                  : t('dashboard.family.statusTotal', {
                      count: numberFormat.format(count),
                      bucket: b,
                    })
              }
              className={`num text-right text-xs tabular-nums ${
                count ? BUCKET_STYLES[COLUMN_BUCKET[b]].text : 'text-muted'
              }`}
            >
              {count === null ? '—' : count === 0 ? '–' : numberFormat.format(count)}
            </span>
          )
        })}

        <span data-testid="family-mix">
          {bucket.statusTotals === null ? (
            <span className="text-xs text-muted">—</span>
          ) : (
            <StatusRatioBar byStatus={familyByStatus(bucket)} total={bucket.totalRequests} />
          )}
        </span>

        <span aria-hidden="true" />
      </button>
    </div>
  )
}

/**
 * The family's bucket sums re-keyed as if they were status codes, so the same
 * `StatusRatioBar` the endpoint rows use can draw the family's mix.
 *
 * One representative code per bucket is enough: the bar only ever asks
 * `bucketFor` which bucket a code belongs to, and these round-trip exactly.
 */
const COLUMN_SPECIMEN: Record<StatusColumn, number> = {
  '200': 200,
  '400': 400,
  '401': 401,
  '402': 402,
  '403': 403,
  '404': 404,
  '405': 405,
  '429': 429,
  '5xx': 500,
}

function familyByStatus(bucket: FamilyBucket): Record<string, number> {
  const out: Record<string, number> = {}
  for (const c of STATUS_COLUMNS) {
    const n = bucket.statusTotals?.[c] ?? 0
    if (n > 0) out[String(COLUMN_SPECIMEN[c])] = n
  }
  return out
}

/**
 * What the family total actually is, said out loud.
 *
 * Three different sentences, because three different things can be true: every
 * endpoint was measured, some were, or none were. Collapsing them into one
 * tooltip is how "0" ends up looking like a reading.
 */
function totalTitle(bucket: FamilyBucket, t: TFunction): string {
  if (bucket.totalRequests === null) return t('dashboard.family.totalNone')
  if (bucket.measuredCount < bucket.endpointCount)
    return t('dashboard.family.totalPartial', {
      measured: bucket.measuredCount,
      total: bucket.endpointCount,
    })
  return t('dashboard.family.totalAll')
}

/** Every health state, at zero. The starting point for counting a family. */
export function emptyHealthCounts(): Record<HealthState, number> {
  return Object.fromEntries(HEALTH_STATES.map((s) => [s, 0])) as Record<HealthState, number>
}
